import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Buildings, Plus, PencilSimple, Trash, CalendarBlank, ChartLineUp,
  FloppyDisk, MagnifyingGlass, DownloadSimple,
} from "@phosphor-icons/react";
import { fmtDate } from "@/lib/dates";

const TABS = [
  { key: "directory",   label: "Directory",         icon: Buildings },
  { key: "consumption", label: "Monthly Consumption", icon: ChartLineUp },
];

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function CustomersPage() {
  const { user } = useAuth();
  const canWrite = ["manager", "supervisor", "admin"].includes(user.role);
  const [tab, setTab] = useState("directory");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/customers");
      setCustomers(res.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (c) => {
    if (!window.confirm(`Remove customer "${c.name}"? If any records exist, the customer is kept but marked inactive.`)) return;
    try {
      const res = await api.delete(`/customers/${c.id}`);
      toast.success(res.data.soft_deleted
        ? `Deactivated (kept for ${res.data.records} historical record${res.data.records === 1 ? "" : "s"})`
        : "Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="customers-page">
      <div className="border-b border-neutral-200 px-10 py-8 bg-white sticky top-0 z-10">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="label-eyebrow">Billing Directory</div>
            <h1 className="font-display text-4xl tracking-tight mt-1">Customers</h1>
            <div className="text-xs text-neutral-500 mt-1">Directory · monthly parts consumption per customer</div>
          </div>
        </div>

        <div className="mt-6 flex border border-neutral-300" data-testid="customers-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`h-11 px-4 text-xs font-mono uppercase tracking-wider border-r last:border-r-0 border-neutral-300 flex-1 inline-flex items-center justify-center gap-2 ${
                  active ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"
                }`}
              >
                <Icon size={13} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-10">
        {tab === "directory" && (
          <DirectoryTab
            customers={customers}
            loading={loading}
            canWrite={canWrite}
            onAdd={() => setDialog({ open: true, editing: null })}
            onEdit={(c) => setDialog({ open: true, editing: c })}
            onDelete={del}
          />
        )}
        {tab === "consumption" && (
          <ConsumptionTab customers={customers} />
        )}
      </div>

      <CustomerDialog
        open={dialog.open}
        editing={dialog.editing}
        onClose={() => setDialog({ open: false, editing: null })}
        onSaved={() => { setDialog({ open: false, editing: null }); load(); }}
      />
    </div>
  );
}


function DirectoryTab({ customers, loading, canWrite, onAdd, onEdit, onDelete }) {
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | main | walkin
  const filtered = customers.filter((c) => {
    if (typeFilter !== "all" && (c.customer_type || "main") !== typeFilter) return false;
    if (q.trim() && !c.name.toLowerCase().includes(q.trim().toLowerCase())) return false;
    return true;
  });
  const mainCount = customers.filter((c) => (c.customer_type || "main") === "main").length;
  const walkinCount = customers.filter((c) => c.customer_type === "walkin").length;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="directory-tab">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder="Filter customers…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 rounded-none border-neutral-400"
            data-testid="customer-filter"
          />
        </div>
        {canWrite && (
          <Button onClick={onAdd} data-testid="add-customer-btn" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 h-10">
            <Plus size={14} weight="bold" className="mr-1" /> Add customer
          </Button>
        )}
      </div>

      {/* Type filter chips */}
      <div className="flex border border-neutral-300 w-fit" data-testid="customer-type-filter">
        {[
          { key: "all", label: `All (${customers.length})` },
          { key: "main", label: `Main (${mainCount})` },
          { key: "walkin", label: `Walk-in (${walkinCount})` },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTypeFilter(t.key)}
            data-testid={`customer-type-tab-${t.key}`}
            className={`h-9 px-4 text-xs font-mono uppercase tracking-wider border-r last:border-r-0 border-neutral-300 ${
              typeFilter === t.key ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="border border-neutral-200 bg-white">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead className="border-b border-neutral-200 bg-neutral-50">
            <tr className="text-left">
              <th className="px-4 py-3 label-eyebrow">Name</th>
              <th className="px-4 py-3 label-eyebrow">Type</th>
              <th className="px-4 py-3 label-eyebrow">Status</th>
              <th className="px-4 py-3 label-eyebrow">Added</th>
              {canWrite && <th className="px-4 py-3 label-eyebrow w-24 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-400 text-xs">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-400">
                {customers.length === 0
                  ? (canWrite ? "No customers yet. Click 'Add customer'." : "No customers configured.")
                  : "No matches."}
              </td></tr>
            )}
            {!loading && filtered.map((c) => {
              const type = c.customer_type || "main";
              return (
                <tr key={c.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${
                        type === "walkin"
                          ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                          : "border-neutral-400 bg-neutral-50 text-neutral-700"
                      }`}
                      data-testid={`customer-type-${c.id}`}
                    >
                      {type === "walkin" ? "Walk-in" : "Main"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${c.active ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-neutral-400 bg-neutral-100 text-neutral-600"}`}>
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500 font-mono">{fmtDate(c.created_at)}</td>
                  {canWrite && (
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => onEdit(c)}
                          className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                          title="Edit"
                          data-testid={`edit-customer-${c.id}`}
                        >
                          <PencilSimple size={13} weight="bold" />
                        </button>
                        <button
                          onClick={() => onDelete(c)}
                          className="p-1.5 border border-neutral-300 hover:border-red-600 hover:bg-red-600 hover:text-white"
                          title="Remove"
                          data-testid={`delete-customer-${c.id}`}
                        >
                          <Trash size={13} weight="bold" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function CustomerDialog({ open, editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [customerType, setCustomerType] = useState("main");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setName(editing?.name || "");
      setActive(editing?.active !== false);
      setAddress(editing?.address || "");
      setPhone(editing?.phone || "");
      setEmail(editing?.email || "");
      setCustomerType(editing?.customer_type || "main");
    }
  }, [open, editing]);
  const submit = async () => {
    if (!name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        active,
        address: address.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        customer_type: customerType,
      };
      if (isEdit) await api.patch(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(isEdit ? "Customer updated" : "Customer added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none border-neutral-900" data-testid="customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isEdit ? `Edit — ${editing?.name}` : "Add customer"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Customer type *</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="customer-type-toggle">
              <button
                type="button"
                onClick={() => setCustomerType("main")}
                data-testid="customer-type-main"
                className={`h-11 border-2 text-sm font-medium transition-colors text-left px-3 ${
                  customerType === "main"
                    ? "border-neutral-900 bg-neutral-900 text-white"
                    : "border-neutral-300 bg-white hover:border-neutral-900"
                }`}
              >
                <div>Main</div>
                <div className={`text-[10px] font-mono uppercase tracking-wider ${customerType === "main" ? "opacity-70" : "text-neutral-500"}`}>Billed via IV / REQ</div>
              </button>
              <button
                type="button"
                onClick={() => setCustomerType("walkin")}
                data-testid="customer-type-walkin"
                className={`h-11 border-2 text-sm font-medium transition-colors text-left px-3 ${
                  customerType === "walkin"
                    ? "border-emerald-700 bg-emerald-700 text-white"
                    : "border-neutral-300 bg-white hover:border-emerald-700"
                }`}
              >
                <div>Walk-in</div>
                <div className={`text-[10px] font-mono uppercase tracking-wider ${customerType === "walkin" ? "opacity-80" : "text-neutral-500"}`}>Counter cash sale</div>
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none border-neutral-400" data-testid="customer-name" placeholder={customerType === "walkin" ? "e.g. Ahmad Bin Ali, Cash Sale" : "e.g. Shell, BHP, Caltex"} />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Address <span className="text-neutral-400 normal-case">(prints on the IV receipt)</span></Label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={2}
              className="w-full rounded-none border border-neutral-400 px-2 py-1.5 text-sm focus:outline-none focus:border-neutral-900"
              data-testid="customer-address"
              placeholder="Building, street, city, postcode"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+60 12-345 6789" className="rounded-none border-neutral-400"
                data-testid="customer-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Email <span className="text-neutral-400 normal-case">(used for DO/Quotation email)</span></Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)}
                type="email" placeholder="orders@customer.com"
                className="rounded-none border-neutral-400" data-testid="customer-email" />
            </div>
          </div>
          <label className="flex items-center gap-2 pt-1 cursor-pointer select-none">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-neutral-900" data-testid="customer-active" />
            <span className="text-sm">Active — available when creating records</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="customer-save" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            <FloppyDisk size={14} weight="bold" className="mr-1" />
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Add customer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ConsumptionTab({ customers }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [mode, setMode] = useState("month"); // "month" | "year"
  const [customerId, setCustomerId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openRow, setOpenRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (mode === "month") params.month = month;
      if (customerId !== "all") params.customer_id = customerId;
      const res = await api.get("/reports/customer-consumption", { params });
      setData(res.data);
      setOpenRow(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, mode, customerId]);

  const years = useMemo(() => {
    const arr = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) arr.push(y);
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = () => {
    if (!data || !data.rows?.length) return toast.error("No data to export");
    let csv, filename;
    if (data.mode === "month") {
      const header = "customer,part,stock_code,group,uom,qty,unit_cost_myr,value_myr\n";
      const rows = data.rows.flatMap((r) =>
        r.parts.map((p) => [
          csvSafe(r.customer_name), csvSafe(p.name), csvSafe(p.stock_code || ""),
          csvSafe(p.group || ""), csvSafe(p.uom || ""),
          p.qty, (p.unit_cost || 0).toFixed(2), (p.value || 0).toFixed(2),
        ].join(","))
      );
      csv = header + rows.join("\n");
      filename = `customer-consumption-${data.year}-${String(data.month).padStart(2, "0")}.csv`;
    } else {
      const header = "customer,total_qty,total_value_myr," + MONTH_NAMES.map((m) => `${m}_qty,${m}_value_myr`).join(",") + "\n";
      const rows = data.rows.map((r) =>
        [csvSafe(r.customer_name), r.qty, r.value.toFixed(2),
          ...r.months.flatMap((m) => [m.qty, m.value.toFixed(2)])].join(",")
      );
      csv = header + rows.join("\n");
      filename = `customer-consumption-${data.year}.csv`;
    }
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="consumption-tab">
      <div className="grid grid-cols-5 gap-3 items-end">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">View</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="rounded-none border-neutral-400" data-testid="mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Single month drill-down</SelectItem>
              <SelectItem value="year">Full year (Jan–Dec)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Year</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="rounded-none border-neutral-400" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {mode === "month" && (
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="rounded-none border-neutral-400" data-testid="month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((n, i) => <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Customer</Label>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger className="rounded-none border-neutral-400" data-testid="customer-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers</SelectItem>
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={exportCsv}
          disabled={!data || !data.rows?.length}
          variant="outline"
          className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white h-10"
          data-testid="export-csv-btn"
        >
          <DownloadSimple size={14} weight="bold" className="mr-1" /> Export CSV
        </Button>
      </div>

      {loading && <div className="text-xs text-neutral-500 p-6 text-center">Loading…</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Customers with usage" value={data.totals.customers} />
            <StatCard label="Total units consumed" value={(data.totals.qty || 0).toLocaleString()} />
            <StatCard label="Total value (MYR)" value={`RM ${(data.totals.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} mono highlight />
          </div>

          {data.mode === "month" ? (
            <MonthTable rows={data.rows} openRow={openRow} setOpenRow={setOpenRow} />
          ) : (
            <YearTable rows={data.rows} />
          )}
        </>
      )}
    </div>
  );
}


function MonthTable({ rows, openRow, setOpenRow }) {
  if (!rows || rows.length === 0) {
    return <div className="border border-neutral-200 bg-neutral-50 px-4 py-10 text-center text-neutral-500 text-sm">No parts consumed by any customer in this month.</div>;
  }
  return (
    <div className="border border-neutral-200 bg-white">
      <table className="w-full text-sm" data-testid="consumption-month-table">
        <thead className="border-b border-neutral-200 bg-neutral-50">
          <tr className="text-left">
            <th className="px-3 py-2 label-eyebrow">Customer</th>
            <th className="px-3 py-2 label-eyebrow text-right">Distinct parts</th>
            <th className="px-3 py-2 label-eyebrow text-right">Units</th>
            <th className="px-3 py-2 label-eyebrow text-right">Value (MYR)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const key = r.customer_id || "__unassigned__";
            const open = openRow === key;
            return (
              <>
                <tr
                  key={key}
                  className={`border-b border-neutral-100 hover:bg-neutral-50/60 cursor-pointer ${open ? "bg-neutral-50" : ""}`}
                  onClick={() => setOpenRow(open ? null : key)}
                  data-testid={`consumption-row-${key}`}
                >
                  <td className="px-3 py-2">
                    <span className="inline-block w-3 mr-1 text-neutral-500 select-none">{open ? "▾" : "▸"}</span>
                    <span className={r.customer_id ? "font-medium" : "italic text-neutral-500"}>{r.customer_name}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{r.parts.length}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.qty}</td>
                  <td className="px-3 py-2 text-right font-mono">RM {r.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
                {open && (
                  <tr key={`${key}-parts`} className="border-b border-neutral-100 bg-neutral-50/40" data-testid={`consumption-parts-${key}`}>
                    <td colSpan={4} className="px-3 py-2">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-neutral-500">
                            <th className="px-2 py-1 text-left label-eyebrow">Part</th>
                            <th className="px-2 py-1 text-left label-eyebrow">Code</th>
                            <th className="px-2 py-1 text-left label-eyebrow">Group</th>
                            <th className="px-2 py-1 text-right label-eyebrow">Qty</th>
                            <th className="px-2 py-1 text-right label-eyebrow">Unit cost (MYR)</th>
                            <th className="px-2 py-1 text-right label-eyebrow">Line total (MYR)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.parts.map((p, i) => (
                            <tr key={`${key}-part-${i}`} className="border-t border-neutral-200">
                              <td className="px-2 py-1">{p.name}</td>
                              <td className="px-2 py-1 font-mono">{p.stock_code || <span className="text-neutral-400">free-text</span>}</td>
                              <td className="px-2 py-1 text-neutral-500">{p.group || "—"}</td>
                              <td className="px-2 py-1 text-right font-mono">{p.qty}</td>
                              <td className="px-2 py-1 text-right font-mono">{p.unit_cost ? `RM ${p.unit_cost.toFixed(2)}` : <span className="text-neutral-400">—</span>}</td>
                              <td className="px-2 py-1 text-right font-mono">{p.value ? `RM ${p.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-neutral-400">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


function YearTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="border border-neutral-200 bg-neutral-50 px-4 py-10 text-center text-neutral-500 text-sm">No consumption recorded for this year.</div>;
  }
  return (
    <div className="border border-neutral-200 bg-white overflow-x-auto">
      <table className="w-full text-xs" data-testid="consumption-year-table">
        <thead className="border-b border-neutral-200 bg-neutral-50">
          <tr>
            <th className="px-2 py-2 label-eyebrow text-left sticky left-0 bg-neutral-50">Customer</th>
            {MONTH_NAMES.map((m) => (
              <th key={m} className="px-2 py-2 label-eyebrow text-right">{m}</th>
            ))}
            <th className="px-2 py-2 label-eyebrow text-right bg-neutral-100">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const key = r.customer_id || "__unassigned__";
            return (
              <tr key={key} className="border-b border-neutral-100 hover:bg-neutral-50/60" data-testid={`year-row-${key}`}>
                <td className={`px-2 py-1.5 sticky left-0 bg-white ${r.customer_id ? "font-medium" : "italic text-neutral-500"}`}>{r.customer_name}</td>
                {r.months.map((m, i) => (
                  <td key={i} className={`px-2 py-1.5 text-right font-mono ${m.value > 0 ? "" : "text-neutral-300"}`}>
                    {m.value > 0
                      ? `${m.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                      : "—"}
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right font-mono font-medium bg-neutral-50">RM {r.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="text-[10px] text-neutral-500 px-3 py-1.5 border-t border-neutral-100 bg-neutral-50">Monthly cells show value in RM (rounded, no fractions). Hover over the Total column for full precision.</div>
    </div>
  );
}


function StatCard({ label, value, mono, highlight }) {
  return (
    <div className={`border px-4 py-3 ${highlight ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 bg-white"}`}>
      <div className={`label-eyebrow ${highlight ? "opacity-70" : ""}`}>{label}</div>
      <div className={`mt-1 font-display text-2xl ${mono ? "font-mono text-xl" : ""}`}>{value}</div>
    </div>
  );
}

function csvSafe(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s) || /^[=+\-@\t\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
